import { useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';

import { DatePeriodInTimestamp } from '@cast/design-system';
import {
  useSearchCriterion,
  RangePickerCriterion,
  RangePickerSelection,
} from '@cast/shared-search-ui';

import { RangePicker, RangePickerProps, RangePickerRef } from 'components/date';
import { DateRangeFilter } from '@cast/shared-date-ui';

type SearchRangePickerProps = Omit<
  RangePickerProps,
  'applyQueryParams' | 'queryParamKeys' | 'filterPresets'
> & {
  criteria: RangePickerCriterion;
  filterPresets: DateRangeFilter[];
  initialPreset: DateRangeFilter;
};

/**
 * Pure helper that derives picker-visible values from a criterion value.
 * Used in both the hook's useMemo and the onChange pre-computation to
 * keep derivation logic consistent and avoid drift.
 */
const derivePickerValues = (
  value: RangePickerSelection,
  initialPreset: DateRangeFilter,
  initialTimezone?: string
) => ({
  fromDate: value.fromDate,
  toDate: value.toDate,
  preset: value.preset || initialPreset,
  timezone: value.timezone || initialTimezone,
});

const useSearchRangePicker = ({
  criteria,
  initialPreset,
  initialTimezone,
}: {
  criteria: RangePickerCriterion;
  initialPreset: DateRangeFilter;
  initialTimezone?: string;
}) => {
  const criterion: any = useSearchCriterion(criteria);

  if (!criterion) {
    throw new Error('SearchRangePicker has to be used in SearchContext');
  }

  return useMemo(
    () => derivePickerValues(criterion.value, initialPreset, initialTimezone),
    // criterion.value sub-properties are listed individually to avoid
    // re-running on unrelated criterion object identity changes. This is the
    // same pattern as the original code; `criterion` is typed `any` so the
    // linter cannot verify the full reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      criterion.value.fromDate,
      criterion.value.preset,
      criterion.value.timezone,
      criterion.value.toDate,
      initialPreset,
      initialTimezone,
    ]
  );
};

export const convertDataRangeToCriterionValue = (
  date: DatePeriodInTimestamp,
  timezone: string,
  preset?: DateRangeFilter
): RangePickerSelection => {
  return preset
    ? { preset, timezone }
    : {
        fromDate: date.from ? dayjs.utc(date.from).toISOString() : undefined,
        toDate: date.to ? dayjs.utc(date.to).toISOString() : undefined,
        timezone,
      };
};

export const SearchRangePicker = ({
  criteria,
  initialPreset,
  initialTimezone,
  ...rest
}: SearchRangePickerProps) => {
  const criterion = useSearchCriterion(criteria);

  if (!criterion) {
    throw new Error('SearchRangePicker has to be used in SearchContext');
  }

  const rangeRef = useRef<RangePickerRef>(null);

  const { fromDate, toDate, preset, timezone } = useSearchRangePicker({
    criteria,
    initialPreset,
    initialTimezone,
  });

  // Signature of the values that drive the picker display.
  // Include preset so that preset↔range transitions are detected.
  const currentSig = `${fromDate ?? ''}|${toDate ?? ''}|${preset ?? ''}|${
    timezone ?? ''
  }`;
  // Tracks the signature of the last value written by our OWN onChange, so the
  // criterion echo-back render is NOT treated as an external change.
  const lastEmittedRef = useRef(currentSig);

  useEffect(() => {
    if (currentSig === lastEmittedRef.current) return;
    // External change (e.g. chart zoom wrote explicit dates, or reset wrote a preset).
    lastEmittedRef.current = currentSig;
    if (fromDate && toDate) {
      rangeRef.current?.setRange({ from: fromDate, to: toDate, timezone });
    } else if (preset) {
      rangeRef.current?.setRange({ preset, timezone });
    }
  }, [currentSig, fromDate, toDate, preset, timezone]);

  return (
    <RangePicker
      ref={rangeRef}
      initialRange={{ from: fromDate, to: toDate }}
      initialPreset={preset}
      initialTimezone={timezone}
      onChange={(date, tz, pickerPreset) => {
        const nextValue = convertDataRangeToCriterionValue(
          date,
          tz,
          pickerPreset
        );
        const {
          fromDate: nf,
          toDate: nt,
          preset: np,
          timezone: ntz,
        } = derivePickerValues(nextValue, initialPreset, initialTimezone);
        const nextSig = `${nf ?? ''}|${nt ?? ''}|${np ?? ''}|${ntz ?? ''}`;
        lastEmittedRef.current = nextSig;
        criterion.setValue?.(nextValue);
      }}
      {...rest}
    />
  );
};
